import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Check,
  Copy,
  ExternalLink,
  Package,
  PackageCheck,
  ShoppingCart,
  Undo2,
  Warehouse,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  cestaPolozky,
  useHromadne,
  useObjednavkyPrehlad,
  useObjednavkyZakazky,
  useUlozPolozku,
  useVratPolozku,
  type ObjednavkyRiadok,
  type ObjednavkyVarianta,
  type ObjednavkySekcia,
  type StavPolozky,
  type ZakazkaPrehlad,
} from "@/api/hooks/useObjednavky";
import { NahradaDialog } from "@/components/objednavky/NahradaDialog";
import { StavBadge } from "@/components/zakazka/StavBadge";
import { skopirujDoSchranky } from "@/lib/heslo";
import { cn } from "@/lib/utils";
import { normalizeNum } from "@/lib/format";
import { KapsuleVariant } from "@/components/zakazka/KapsuleVariant";
import { odkazNaNastavenia } from "@/components/nastavenia/kategorie";

const STAV_LABEL: Record<StavPolozky, string> = {
  caka: "Čaká",
  rezervovane: "Beriem si to",
  objednane: "Objednané",
  prijate: "Prijaté",
  zo_skladu: "Zo skladu",
};

const STAV_VARIANT: Record<
  StavPolozky,
  "secondary" | "default" | "dokoncena" | "vo_vyrobe" | "ponuka"
> = {
  caka: "secondary",
  rezervovane: "ponuka",
  objednane: "vo_vyrobe",
  prijate: "dokoncena",
  zo_skladu: "default",
};

/** Kedy sa to stalo — dátum stačí, hodina nikoho nezaujíma. */
function den(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("sk-SK");
}

/**
 * Objednávky — čo treba na zákazku kúpiť a čo už je zaobstarané.
 *
 * Rieši konkrétny problém: *„aby sa nestalo, že niekto objedná a nevie,
 * že to objednal druhý."* Preto je to **stránka cez všetky zákazky vo
 * výrobe**, nie tab v editore — pokrok je vidno bez otvárania zákazky.
 *
 * Súpis sa dopočítava z dielcov (rovnaký výpočet ako PDF „Súpis pre
 * objednávky"), uložený je len stav objednania. Stav **„Beriem si to"**
 * je jediný, ktorý dvojitý nákup skutočne zabráni — ostatné ho len
 * dodatočne ukážu, keď sa už stal.
 *
 * Výber zákazky žije v URL (`?zakazka_id=`), takže sa dá poslať odkazom
 * a z editora sem vedie položka v `⋮` menu.
 */
export function ObjednavkyPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const vybrana = params.get("zakazka_id") ?? undefined;

  const prehlad = useObjednavkyPrehlad();
  const zakazky = prehlad.data?.data ?? [];

  // Bez výberu ukáž prvú — prázdna pravá polovica by vyzerala ako chyba
  const aktivna = vybrana ?? zakazky[0]?.zakazka_id;

  // Výber variant (KOLO 206) — v adrese, aby sa dal poslať odkazom
  const varianty = params.get("varianty")?.split(",").filter(Boolean);
  // Riadok otvorenej zákazky vľavo počíta z výberu vpravo (KOLO 218) —
  // dovtedy ukazoval predvolené varianty („3/8") vedľa detailu „3/5".
  // Ten istý kľúč dotazu ako detail, takže sa nič nenačíta dvakrát.
  const vyberDetail = useObjednavkyZakazky(
    varianty?.length ? aktivna : undefined,
    varianty,
  ).data?.data;
  const poctyAktivnej = vyberDetail
    ? { vyriesenych: vyberDetail.vyriesenych, celkom: vyberDetail.celkom }
    : undefined;

  function vyber(id: string): void {
    const next = new URLSearchParams(params);
    next.set("zakazka_id", id);
    next.delete("varianty");
    setParams(next, { replace: true });
  }

  function vyberVarianty(ids: string[] | null): void {
    const next = new URLSearchParams(params);
    if (aktivna) next.set("zakazka_id", aktivna);
    if (ids) next.set("varianty", ids.join(","));
    else next.delete("varianty");
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-3xl">
          <ShoppingCart className="h-7 w-7 text-primary" />
          Objednávky
        </h1>
        <p className="text-sm text-muted-foreground">
          Čo treba na zákazku kúpiť, kto to už objednal a odkiaľ.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <ZoznamZakaziek
          zakazky={zakazky}
          nacitava={prehlad.isLoading}
          aktivna={aktivna}
          poctyAktivnej={poctyAktivnej}
          onVyber={vyber}
        />
        {aktivna ? (
          <DetailZakazky
            key={aktivna}
            zakazkaId={aktivna}
            varianty={varianty}
            onVarianty={vyberVarianty}
          />
        ) : (
          <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            {prehlad.isLoading
              ? "Načítavam…"
              : "Žiadna zákazka nie je v príprave ani vo výrobe — objednávať zatiaľ netreba."}
          </p>
        )}
      </div>
    </div>
  );
}

function ZoznamZakaziek({
  zakazky,
  nacitava,
  aktivna,
  poctyAktivnej,
  onVyber,
}: {
  zakazky: ZakazkaPrehlad[];
  nacitava: boolean;
  aktivna: string | undefined;
  /** Počty otvorenej zákazky podľa výberu variant vpravo, ak nejaký je. */
  poctyAktivnej?: { vyriesenych: number; celkom: number };
  onVyber: (id: string) => void;
}): JSX.Element {
  return (
    <section className="space-y-2 rounded-lg border bg-card p-3">
      <h2 className="text-sm font-medium text-muted-foreground">
        Zákazky v príprave a vo výrobe
      </h2>
      {nacitava ? (
        <Skeleton className="h-24 w-full" />
      ) : zakazky.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Žiadna zákazka v príprave ani vo výrobe.
        </p>
      ) : (
        <ul className="space-y-1">
          {zakazky.map((riadok) => {
            const z =
              riadok.zakazka_id === aktivna && poctyAktivnej
                ? { ...riadok, ...poctyAktivnej }
                : riadok;
            const hotovo = z.celkom > 0 && z.vyriesenych === z.celkom;
            return (
              <li key={z.zakazka_id}>
                <button
                  type="button"
                  onClick={() => onVyber(z.zakazka_id)}
                  className={cn(
                    "w-full rounded-md border px-2 py-1.5 text-left text-sm transition-colors",
                    z.zakazka_id === aktivna
                      ? "border-primary bg-background-alt"
                      : "border-transparent hover:bg-background-alt/60",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono">{z.cislo_zakazky}</span>
                    {z.nazov ? (
                      <span className="min-w-0 truncate text-muted-foreground">
                        {z.nazov}
                      </span>
                    ) : null}
                    {/* Ktorá sa ešte pripravuje a ktorá sa už reže (KOLO 204) */}
                    <span className="ml-auto shrink-0">
                      <StavBadge stav={z.stav} />
                    </span>
                  </span>
                  <span
                    className={cn(
                      "mt-0.5 block text-xs",
                      hotovo
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-muted-foreground",
                    )}
                  >
                    {z.celkom === 0
                      ? "bez položiek"
                      : `${z.vyriesenych}/${z.celkom} zaobstarané`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function DetailZakazky({
  zakazkaId,
  varianty,
  onVarianty,
}: {
  zakazkaId: string;
  varianty?: string[];
  /** `null` = späť na predvolené servera. */
  onVarianty: (ids: string[] | null) => void;
}): JSX.Element {
  const query = useObjednavkyZakazky(zakazkaId, varianty);
  const data = query.data?.data;

  if (query.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!data) {
    return (
      <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Zákazka nemá výrobný ani aktívny výpočet — nie je z čoho súpis
        spraviť.
      </p>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-2 rounded-lg border bg-card px-4 py-2 text-sm">
        <span className="font-mono text-base">{data.cislo_zakazky}</span>
        {data.varianty.length === 1 && data.varianty[0] ? (
          <span className="text-muted-foreground">
            {data.varianty[0].nazov}
            {data.varianty[0].je_vyrobna
              ? " (výrobná)"
              : " (cenový — výrobná neexistuje)"}
          </span>
        ) : null}
        <span className="ml-auto font-medium">
          {data.vyriesenych}/{data.celkom} zaobstarané
        </span>
      </div>

      {data.varianty.length > 1 ? (
        <VyberVariantObjednavok
          varianty={data.varianty}
          vlastny={Boolean(varianty?.length)}
          onZmena={onVarianty}
        />
      ) : null}

      {data.sekcie.map((s) => (
        <SekciaDodavatela key={s.kluc} sekcia={s} zakazkaId={zakazkaId} />
      ))}

      {data.mimo_supisu.length > 0 ? (
        <section className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
          <h3 className="text-sm font-semibold">Už nie je v zákazke</h3>
          <p className="text-xs text-muted-foreground">
            Tieto položky sa zo súpisu stratili (dielce sa medzitým zmenili),
            ale niekto s nimi už niečo spravil. Nechávame ich na očiach —
            peniaze mohli byť vydané.
          </p>
          <ul className="space-y-1 text-sm">
            {data.mimo_supisu.map((p) => (
              <li key={p.material_id ?? p.vlastna_kluc ?? p.kod} className="flex gap-2">
                <span className="font-mono">{p.kod ?? "—"}</span>
                <span className="text-muted-foreground">{p.nazov ?? ""}</span>
                <Badge variant={STAV_VARIANT[p.stav]} className="ml-auto">
                  {STAV_LABEL[p.stav]}
                </Badge>
                {p.objednal ? (
                  <span className="text-xs text-muted-foreground">
                    {p.objednal} {den(p.objednane_at)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.sekcie.length === 0 && data.mimo_supisu.length === 0 ? (
        <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          Súpis je prázdny — vo výpočte nie sú žiadne materiály.
        </p>
      ) : null}
    </section>
  );
}

/**
 * Z ktorých variant je súpis (KOLO 206) — časti zákazky sa nakupujú spolu,
 * tabule a tyče sa rátajú z dielcov všetkých zaškrtnutých naraz. Stav
 * objednania je spoločný pre celú zákazku.
 */
function VyberVariantObjednavok({
  varianty,
  vlastny,
  onZmena,
}: {
  varianty: ObjednavkyVarianta[];
  /** Výber je z adresy, nie predvolený servera — ukáž Predvolené. */
  vlastny: boolean;
  onZmena: (ids: string[] | null) => void;
}): JSX.Element {
  return (
    <KapsuleVariant
      className="rounded-lg border bg-card px-4 py-2"
      ariaLabel="Varianty v súpise"
      varianty={varianty.map((v) => ({
        id: v.id,
        nazov: v.nazov,
        cislo: v.cislo,
        vybrana: v.vybrana,
        druh: v.je_vyrobna ? "výrobná" : null,
      }))}
      onZmena={onZmena}
      predvolene={
        vlastny
          ? {
              title: "Celá výroba, inak aktívny výpočet",
              onClick: () => onZmena(null),
            }
          : null
      }
    />
  );
}

function SekciaDodavatela({
  sekcia,
  zakazkaId,
}: {
  sekcia: ObjednavkySekcia;
  zakazkaId: string;
}): JSX.Element {
  const hromadne = useHromadne(zakazkaId);
  const nezaobstarane = sekcia.riadky.filter(
    (r) => r.stav === "caka" || r.stav === "rezervovane",
  );
  // Vlastné položky (KOLO 211) eshop nemajú — o odkaze rozhodujú len
  // položky z cenníka; sekcia len s vlastnými (Elektro Novák) nič nehlási
  const zCennika = sekcia.riadky.filter((r) => r.material_id != null);
  const bezOdkazu =
    sekcia.kluc !== "_ostatni" &&
    zCennika.length > 0 &&
    zCennika.every((r) => !r.eshop_url);

  // Vlastné položky (KOLO 211) kód nemajú — sekcia len s nimi nemá čo kopírovať
  const kody = sekcia.riadky.map((r) => r.eshop_kod || r.kod).filter(Boolean);

  async function kopirujKody(): Promise<void> {
    // B2B eshopy vedia hromadné pridanie do košíka vložením zoznamu kódov —
    // toto je realistická náhrada za „vlož do košíka", ktorá bez API eshopu
    // spraviť nejde.
    //
    // Ide **kód dodávateľa**, nie náš `kod`: naša identita je často
    // z Daexu (`8681_SM_18DTD`) alebo zlepenec dekoru a hrúbky
    // (`W960_18mm_SM`) a v cudzom košíku by nesedela ani jedna položka.
    const ok = await skopirujDoSchranky(kody.join("\n"));
    toast(
      ok
        ? { title: `Skopírované (${kody.length} kódov)` }
        : {
            variant: "destructive",
            title: "Nepodarilo sa skopírovať",
            description: "Označ kódy myšou a skopíruj ručne.",
          },
    );
  }

  async function objednajVsetko(): Promise<void> {
    try {
      const r = await hromadne.mutateAsync({
        material_ids: nezaobstarane.flatMap((x) => (x.material_id ? [x.material_id] : [])),
        vlastne_kluce: nezaobstarane.flatMap((x) =>
          x.material_id == null && x.vlastna_kluc ? [x.vlastna_kluc] : [],
        ),
        stav: "objednane",
        // Dodávateľ vlastných položiek (KOLO 211) nemá kľúč eshopu —
        // „vlastny:zamocnictvo kovac“ by sa zapísal ako odkiaľ sa objednalo
        eshop: sekcia.kluc.startsWith("vlastny:") ? sekcia.label.slice(0, 50) : sekcia.kluc,
      });
      toast({ title: `Označené ako objednané: ${r.data.hotovo}` });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Hromadná zmena neprešla",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <section className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <h3 className="font-medium">{sekcia.label}</h3>
        <span className="text-xs text-muted-foreground">
          {sekcia.riadky.length} položiek
        </span>
        {/* Bez tohto je absencia odkazu na nerozoznanie od chyby — presne
            na to sa user pýtal. Pri „neurčenom dodávateľovi" sa nezobrazí,
            tam nie je čo nastaviť. */}
        {bezOdkazu ? (
          <span className="text-xs text-muted-foreground">
            Odkaz na eshop nie je nastavený —{" "}
            <Link
              to={odkazNaNastavenia("ceny")}
              className="text-primary hover:underline"
            >
              nastaviť
            </Link>
          </span>
        ) : null}
        <div className="ml-auto flex gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void kopirujKody()}
            disabled={kody.length === 0}
            title={
              kody.length === 0
                ? "Vlastné položky kód nemajú"
                : "Skopírovať kódy — väčšina B2B eshopov vie hromadné pridanie do košíka"
            }
          >
            <Copy className="h-3.5 w-3.5" />
            Kódy
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={nezaobstarane.length === 0 || hromadne.isPending}
            onClick={() => void objednajVsetko()}
            title={
              nezaobstarane.length === 0
                ? "Všetko je už zaobstarané"
                : `Označiť ${nezaobstarane.length} položiek ako objednané`
            }
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            Objednané ({nezaobstarane.length})
          </Button>
        </div>
      </div>
      <ul className="divide-y">
        {sekcia.riadky.map((r) => (
          <RiadokPolozky
            key={r.material_id ?? r.vlastna_kluc}
            riadok={r}
            zakazkaId={zakazkaId}
          />
        ))}
      </ul>
    </section>
  );
}

function RiadokPolozky({
  riadok,
  zakazkaId,
}: {
  riadok: ObjednavkyRiadok;
  zakazkaId: string;
}): JSX.Element {
  const uloz = useUlozPolozku(zakazkaId);
  const vrat = useVratPolozku(zakazkaId);
  const [nahradaOtvorena, setNahradaOtvorena] = useState(false);

  const kto = useMemo(() => {
    if (riadok.stav === "rezervovane" && riadok.rezervoval) {
      return `${riadok.rezervoval} · ${den(riadok.rezervovane_at)}`;
    }
    if (riadok.objednal) {
      return `${riadok.objednal} · ${den(riadok.objednane_at)}${
        riadok.eshop ? ` · ${riadok.eshop}` : ""
      }`;
    }
    return "";
  }, [riadok]);

  async function nastav(stav: StavPolozky): Promise<void> {
    try {
      await uloz.mutateAsync({
        polozka: cestaPolozky(riadok),
        data: { stav, eshop: stav === "objednane" ? riadok.eshop : undefined },
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Zmena neprešla",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-2 px-4 py-2 text-sm">
      <span className="w-24 shrink-0 text-right font-mono">
        {/* Server posiela Decimal s bodkou a nulami („15.500“ pri vlastnej
            položke, KOLO 211) — tu ako všade v appke: 15,5 */}
        {normalizeNum(riadok.mnozstvo).replace(".", ",")} {riadok.mj}
      </span>
      {/* `min-w-[12rem]`: na mobile (KOLO 212) stav a tlačidlá zalomí pod
          názov — s `min-w-0` sa názov tlačil po slove do úzkeho stĺpca. */}
      <span className="min-w-[12rem] flex-1">
        {riadok.material_id == null ? (
          // Vlastná položka mimo cenníka (KOLO 211) — kód nemá
          <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
            vlastná
          </span>
        ) : (
          <span className="font-mono text-xs text-muted-foreground">
            {riadok.kod}
          </span>
        )}{" "}
        {riadok.nazov}
        {riadok.nahrada_nazov || riadok.nahrada_text ? (
          <span className="block text-xs text-amber-700 dark:text-amber-400">
            namiesto toho: {riadok.nahrada_nazov ?? riadok.nahrada_text}
          </span>
        ) : null}
        {riadok.poznamka ? (
          <span className="block text-xs text-muted-foreground">
            {riadok.poznamka}
          </span>
        ) : null}
        {/* Bez tohto vyzerá záves a podložka ako dve nesúvisiace položky
            a user to číta ako „set máš rozbitý" (KOLO 103). Rozpad je
            zámer — objednáva sa, čo sa reálne kupuje — ale musí byť
            vidieť, odkiaľ komponent je. */}
        {riadok.zo_setu.length > 0 ? (
          <span className="block text-xs text-muted-foreground">
            zo setu: {riadok.zo_setu.join(", ")}
          </span>
        ) : null}
      </span>

      {kto ? (
        <span className="text-xs text-muted-foreground">{kto}</span>
      ) : null}
      <Badge variant={STAV_VARIANT[riadok.stav]}>
        {STAV_LABEL[riadok.stav]}
      </Badge>

      {/* Odkaz je len keď adresa eshopu existuje (uložená alebo
          predvolená) — inak by user klikal do prázdna.

          Je to tlačidlo s textom, nie holá ikona: medzi štítkom stavu
          a „Zmeniť" sa 16 px šípka prehliadne a user sa pýtal, či tam
          odkaz vôbec má byť. */}
      {riadok.eshop_url ? (
        <Button asChild size="sm" variant="outline">
          <a
            href={riadok.eshop_url}
            target="_blank"
            rel="noopener noreferrer"
            title="Vyhľadať v eshope dodávateľa"
            aria-label={`Vyhľadať ${riadok.kod} v eshope`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Eshop
          </a>
        </Button>
      ) : null}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={uloz.isPending || vrat.isPending}
            aria-label={`Zmeniť stav — ${riadok.kod || riadok.nazov}`}
          >
            Zmeniť
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => void nastav("rezervovane")}>
            <Package className="h-4 w-4" />
            Beriem si to
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void nastav("objednane")}>
            <ShoppingCart className="h-4 w-4" />
            Objednané
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void nastav("prijate")}>
            <PackageCheck className="h-4 w-4" />
            Prijaté
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void nastav("zo_skladu")}>
            <Warehouse className="h-4 w-4" />
            Zo skladu
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setNahradaOtvorena(true)}>
            <Check className="h-4 w-4" />
            Náhrada / poznámka
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={riadok.stav === "caka"}
            onSelect={() => void vrat.mutateAsync(cestaPolozky(riadok))}
          >
            <Undo2 className="h-4 w-4" />
            Vrátiť na čaká
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {nahradaOtvorena ? (
        <NahradaDialog
          riadok={riadok}
          zakazkaId={zakazkaId}
          onClose={() => setNahradaOtvorena(false)}
        />
      ) : null}
    </li>
  );
}
